import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { AuthService } from '../interfaces';
import { CORE_CONFIG } from '../../../tokens';
import { CoreConfig } from '../../../core.config';
import { CacheService } from '../../misc';
import { IUserService } from '../../public';
import { lastValueFrom } from 'rxjs';

interface AuthResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

interface AuthError {
  error?: string;
  error_description?: string;
}

@Injectable()
export class SsoAzureAuthService extends AuthService {
  readonly isSso = true;

  private get accessToken(): string {
    return localStorage.getItem('access_token') as string;
  }

  private set accessToken(value: string) {
    localStorage.setItem('access_token', value);
  }

  private get refreshToken(): string {
    return localStorage.getItem('refresh_token') as string;
  }

  private set refreshToken(value: string) {
    localStorage.setItem('refresh_token', value);
  }

  private get accessTokenExpireAt(): number {
    return Number(localStorage.getItem('access_token_expire_at'));
  }

  private set accessTokenExpireAt(value: number) {
    localStorage.setItem('access_token_expire_at', value.toString());
  }

  private get codeVerifier(): string {
    return localStorage.getItem('pkce_code_verifier') as string;
  }

  private set codeVerifier(value: string) {
    localStorage.setItem('pkce_code_verifier', value);
  }

  private get state(): string {
    return localStorage.getItem('pkce_state') as string;
  }

  private set state(value: string) {
    localStorage.setItem('pkce_state', value);
  }

  private get failedAttempts(): number {
    return +(localStorage.getItem('failed_attempts') ?? 0);
  }

  private set failedAttempts(value: number) {
    if (value) {
      localStorage.setItem('failed_attempts', value.toString());
    } else {
      localStorage.removeItem('failed_attempts');
    }
  }

  private get redirectUri(): string {
    return `${location.protocol}//${location.host}${this.router.createUrlTree(this.config.authRoute).toString()}`;
  }

  private refreshTokenPromise: Promise<void> | null = null;

  constructor(
    private router: Router,
    private http: HttpClient,
    @Inject(CORE_CONFIG) private config: CoreConfig,
    private httpClient: HttpClient,
    private cacheService: CacheService,
    private userService: IUserService,
  ) {
    super();

    if (!this.config || !this.config.ssoAzureAuthConfig) {
      throw new Error('SSO configuration is missing');
    }
  }

  get isAuthenticated(): boolean | Promise<boolean> {
    if (!this.accessToken) {
      return false;
    }

    if (this.isTokenExpired) {
      return this.ensureValidToken().then(() => !!this.accessToken);
    }

    return true;
  }

  get isTokenExpired(): boolean {
    return this.accessTokenExpireAt < Date.now();
  }

  get token(): string {
    return this.accessToken;
  }

  private authLoopPrevented = false;

  async authenticate() {
    const code = this.getRouteParam('code');
    const error = this.getRouteParam('error');
    const errorDescription = this.getRouteParam('error_description');

    try {
      if (code) {
        if (this.state !== this.getRouteParam('state')) {
          this.failedAttempts++;
          throw new Error('Invalid state');
        }

        await this.getAccessToken(code);

        try {
          this.userService.me();
          this.failedAttempts = 0;
        } catch {
          this.failedAttempts++;
        }
      } else if (error) {
        this.failedAttempts++;

        if (error === 'login_required' && !this.authLoopPrevented) {
          this.authLoopPrevented = true;
          await this.authorize('select_account');
          return;
        }

        throw new Error(errorDescription as string);
      } else {
        this.failedAttempts = 0;
        await this.authorize('none');
      }
    } catch (e) {
      console.log('Error : ', e);
      throw e;
    }
  }

  async deauthenticate(manual = false) {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('access_token_expire_at');
    localStorage.removeItem('pkce_code_verifier');
    localStorage.removeItem('pkce_state');
    localStorage.removeItem('failed_attempts');

    try {
      this.cacheService.clear();

      if (manual) {
        const postLogoutRedirectUri = encodeURIComponent(`${window.location.protocol}//${window.location.host}/auth?disconnected=true`);
        const logoutUrl = `${this.config.ssoAzureAuthConfig!.authority}/logout?post_logout_redirect_uri=${postLogoutRedirectUri}`;
        window.location.href = logoutUrl;
      } else {
        await this.router.navigate(this.config.authRoute, { queryParams: { disconnected: true } });
      }
    } catch (e) {
      console.error('Error during deauthentication:', e);
    }
  }

  private async authorize(prompt: 'none' | 'select_account') {
    this.state = this.generateRandomString();
    this.codeVerifier = this.generateRandomString();

    const queryParams: Record<string, string> = {
      client_id: this.config.ssoAzureAuthConfig!.clientId,
      response_type: 'code',
      prompt,
      scope: this.config.ssoAzureAuthConfig!.scope.join(' '),
      redirect_uri: this.redirectUri,
      response_mode: 'query',
      state: this.state,
      code_challenge_method: 'S256',
      code_challenge: await this.pkceChallengeFromVerifier(this.codeVerifier),
    };

    const params = this.buildQueryParam(queryParams);
    location.replace(`${this.config.ssoAzureAuthConfig!.authority}/authorize?${encodeURI(params)}`);
  }

  private async getAccessToken(code: string, refresh?: boolean) {
    const queryParams = new Map<string, string>([
      ['grant_type', refresh ? 'refresh_token' : 'authorization_code'],
      ['client_id', this.config.ssoAzureAuthConfig!.clientId],
      ['scope', this.config.ssoAzureAuthConfig!.scope.join(' ')],
    ]);

    if (refresh) {
      queryParams.set('refresh_token', code);
    } else {
      queryParams.set('code', code);
      queryParams.set('code_verifier', this.codeVerifier);
      queryParams.set('redirect_uri', this.redirectUri);
    }

    const params = this.buildQueryParam(Object.fromEntries(queryParams));
    try {
      const response = await lastValueFrom(
        this.httpClient.post<AuthResponse>(`${this.config.ssoAzureAuthConfig!.authority}/token`, encodeURI(params), {
          headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded'),
        }),
      );

      this.accessToken = response.access_token;
      this.refreshToken = response.refresh_token;
      this.accessTokenExpireAt = Date.now() + Number(response.expires_in) * 1000;
    } catch (error: unknown) {
      const authError = error as AuthError;
      if (authError.error) {
        throw new Error(authError.error_description || 'Authentication error');
      }
      throw new Error('Unknown error');
    }
  }

  private buildQueryParam(queryParams: Record<string, string>): string {
    return Reflect.ownKeys(queryParams)
      .reduce((acc: string, key: string | symbol) => `${acc}&${String(key)}=${String(Reflect.get(queryParams, key))}`, '')
      .substr(1);
  }

  private generateRandomString(): string {
    const array = new Uint32Array(28);
    window.crypto.getRandomValues(array);
    return Array.from(array, (dec) => ('0' + dec.toString(16)).substr(-2)).join('');
  }

  private async pkceChallengeFromVerifier(verifier: string): Promise<string> {
    return this.base64urlencode(await this.sha256(verifier));
  }

  private sha256(plain: string): Promise<ArrayBuffer> {
    const encoder = new TextEncoder();
    const data = encoder.encode(plain);
    return window.crypto.subtle.digest('SHA-256', data);
  }

  private base64urlencode(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    return btoa(String.fromCharCode(...bytes))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  private parseJwt(token: string): Record<string, unknown> {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );

    return JSON.parse(jsonPayload) as Record<string, unknown>;
  }

  private getRouteParam(key: string): string | null {
    return this.router.routerState.snapshot.root.queryParamMap.get(key);
  }

  private ensureValidToken(): Promise<void> {
    if (this.accessToken && !this.isTokenExpired) {
      return Promise.resolve();
    }

    if (!this.refreshTokenPromise) {
      this.refreshTokenPromise = this.getAccessToken(this.refreshToken, true)
        .then(() => {
          this.refreshTokenPromise = null;
        })
        .catch((err) => {
          this.refreshTokenPromise = null;
          void this.deauthenticate();
          throw err;
        });
    }

    return this.refreshTokenPromise;
  }
}
